import { FileArrowDownSolid, FileArrowUpSolid, MagnifyingGlassSolid } from '@allocab/allocab-icons';
import { useMemo, useState } from 'react';

import { InvoiceDownloadButton } from './InvoiceDownloadButton';
import { Invoice } from './types';
import { Link } from '../../components/atomic/Link';
import { TextInput } from '../../components/atomic/TextInput';
import { PrimaryButton } from '../../components/button/PrimaryButton';
import { Table } from '../../components/table/Table';
import { TableColumn } from '../../components/table/types';
import { useScheduleManyInvoices } from '../../hooks/useScheduleManyInvoices';
import { ToastManager } from '../../services/toast/utils';
import { t } from '../../services/translation/translation';
import { formatDateForParisTZ } from '../../utils/date';
import { formatPrice } from '../../utils/price';

const columns: TableColumn<Invoice>[] = [
  {
    name: t('common.invoice-number'),
    render: invoice => (
      <div className="flex flex-col items-start">
        <span>{invoice.invoiceNumber}</span>
        <span className="text-xs opacity-50 font-normal">{invoice.name}</span>
      </div>
    ),
  },
  {
    name: t('common.date'),
    render: invoice => formatDateForParisTZ(invoice.creationDate, t('date.date-short'), false),
  },
  {
    name: t('common.driver'),
    render: invoice =>
      invoice.driverName ? <Link to={`/drivers/${invoice.driverKey}`}>{invoice.driverName}</Link> : '--',
  },
  {
    name: t('common.type'),
    render: invoice => (
      <div className="space-y-1">
        <span>{invoice.subject}</span>
        {invoice.type === 'in' ? (
          <span className="flex items-center gap-2">
            <FileArrowUpSolid height={16} width={16} className="fill-current" />
            {t('invoicing-route.sent')}
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <FileArrowDownSolid height={16} width={16} className="fill-current" />
            {t('invoicing-route.received')}
          </span>
        )}
      </div>
    ),
  },
  {
    name: t('invoicing-route.ttc-amount'),
    render: invoice => {
      const amount = invoice.totalTTC * (invoice.type === 'out' && invoice.totalTTC !== 0 ? -1 : 1);

      return (
        <div className="inline-flex flex-col items-start w-36">
          <span>{formatPrice(amount)}</span>
          <span className="text-[10px] opacity-50 font-normal">
            {t('invoicing-route.vat-included', { price: formatPrice(invoice.totalTTC - invoice.totalHT) })}
          </span>
        </div>
      );
    },
  },
  { name: '', render: invoice => <InvoiceDownloadButton invoice={invoice} /> },
];

type Props = {
  invoices: Invoice[];
  isLoading: boolean;
  isFetching: boolean;
};

export const InvoicesTable = ({ invoices, isLoading, isFetching }: Props) => {
  const [driverQuery, setDriverQuery] = useState('');
  const [selectedInvoices, setSelectedInvoices] = useState<Invoice[]>([]);

  const { mutate: downloadManyInvoices, isLoading: isDownloadingMany } = useScheduleManyInvoices();
  const handleDownloadManyInvoices = () => {
    downloadManyInvoices(
      { invoiceIds: selectedInvoices.map(invoice => invoice.id) },
      {
        onSuccess: () => {
          ToastManager.show({ message: t('invoicing-route.download-many-success'), severity: 'success', timer: 10000 });
          setSelectedInvoices([]);
        },
      },
    );
  };

  const filteredInvoices = useMemo<Invoice[]>(() => {
    return invoices
      .filter(i => i.invoiceNumber !== 'Pas de facture')
      .filter(i =>
        driverQuery.length > 0
          ? !!i.driverName?.toLocaleLowerCase().includes(driverQuery.toLocaleLowerCase()) ||
            !!i.subject?.toLocaleLowerCase().includes(driverQuery.toLocaleLowerCase())
          : true,
      )
      .sort((a, b) => (a.creationDate > b.creationDate ? 1 : -1));
  }, [driverQuery, invoices]);

  return (
    <div className="space-y-2">
      <TextInput
        placeholder={t('invoicing-route.search-driver')}
        className="min-w-full xl:w-56 xl:min-w-0"
        value={driverQuery}
        onChange={e => setDriverQuery(e.currentTarget.value)}
        icon={<MagnifyingGlassSolid width={16} height={16} className="fill-current" />}
      />
      <Table
        identifier="id"
        columns={columns}
        data={filteredInvoices}
        isLoading={isLoading}
        isFetching={isFetching}
        title={t('common.invoices')}
        context={undefined}
        selectedItems={selectedInvoices}
        onSelectedItemsChange={setSelectedInvoices}
        selectionAction={
          <PrimaryButton
            text={t('common.download')}
            onClick={handleDownloadManyInvoices}
            isLoading={isDownloadingMany}
          />
        }
        totalItems={invoices.length}
      />
    </div>
  );
};
